import { Component, Input, Output, EventEmitter, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef, AfterContentInit, OnChanges, SimpleChanges, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NotificationService } from '@shared/services/notification.service';
import { TablePreferencesService } from '@shared/services/table-preferences.service';
import { DataPaginationComponent } from '../data-pagination/data-pagination.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { LoadingStateComponent } from '../loading-state/loading-state.component';

export interface TableColumn {
  key: string;
  header: string;
  width?: string;
  minWidth?: string;
  sortable?: boolean;
  hideable?: boolean;
  visible?: boolean;
  resizable?: boolean;
  exportValue?: (row: any) => string;
  exportable?: boolean;
  editable?: boolean;
  editType?: 'text' | 'number' | 'select' | 'multiSelect' | 'date';
  editOptions?: { value: any; label: string }[];
  editValue?: (row: any) => any;
  mobileHidden?: boolean;
  mobileTitle?: boolean;
  mobileLabel?: string;
  mobileOrder?: number;
}

export interface PaginationConfig {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface FilterChip {
  key: string;
  label: string;
  value?: any;
  removable?: boolean;
}

export type SortEntry = { sortBy: string; direction: 'asc' | 'desc' };

export interface CellEditSaveEvent<T> {
  row: T;
  column: string;
  oldValue: any;
  newValue: any;
}

interface EditingCell<T> {
  row: T;
  column: string;
  oldValue: any;
  value: any;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatButtonModule,
    MatIconModule,
    DragDropModule,
    DataPaginationComponent,
    EmptyStateComponent,
    LoadingStateComponent
  ],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent<T> implements AfterContentInit, OnChanges, OnDestroy {
  @Input() columns: TableColumn[] = [];
  @Input() set data(value: T[] | null | undefined) {
    this.dataSource.data = this.getSortedData(value ?? []);
    this.cdr.markForCheck();
  }
  @Input() isLoading = false;
  @Input() errorMessage = '';
  @Input() noDataMessage = 'No se encontraron registros';
  @Input() paginationConfig?: PaginationConfig;
  @Input() pageSizeOptions?: number[] = [5, 10, 25, 50, 100];
  @Input() showPageSizeSelector = false;
  @Input() tableHeight = '65vh';
  @Input() enableRowHover = true;
  @Input() enableDragDrop = false;
  @Input() cellTemplate?: TemplateRef<any>;
  @Input() rowDetailTemplate?: TemplateRef<any>;
  @Input() expandedRow: T | null = null;
  @Input() headerTemplate?: TemplateRef<any>;
  @Input() initialSort?: { sortBy: string; direction: 'asc' | 'desc' };
  @Input() enableHeaderSort = true;
  @Input() serverSideSort = false;

  /** ID único de la tabla para persistir preferencias en localStorage */
  @Input({ required: true }) tableId!: string;

  /** Densidad de las filas */
  @Input() density: 'compact' | 'normal' | 'comfortable' = 'normal';

  /** Habilita sort multi-columna con Shift+Click */
  @Input() multiSort = false;

  @Input() editableColumns: string[] = [];
  @Input() savingCell?: { row: any; column: string };

  selectedPageSize = 5;
  localColumns: TableColumn[] = [];

  /** Estado de sort multi-columna */
  sortState: SortEntry[] = [];

  /** Compat: primer sort del estado (para consumidores simples) */
  get currentSortBy(): string { return this.sortState[0]?.sortBy ?? ''; }
  get currentSortDirection(): 'asc' | 'desc' { return this.sortState[0]?.direction ?? 'asc'; }

  /** Resize tracking */
  private resizeColKey: string | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private boundMouseMove: (e: MouseEvent) => void;
  private boundMouseUp: (e: MouseEvent) => void;
  readonly dataSource = new MatTableDataSource<T>();
  readonly hasDetailTemplate = () => !!this.rowDetailTemplate;

  Math = Math;

  @Output() pageChange = new EventEmitter<{ page: number }>();
  @Output() pageSizeChange = new EventEmitter<{ pageSize: number }>();
  @Output() sortChange = new EventEmitter<{ sortBy: string; direction: 'asc' | 'desc' }>();
  @Output() sortStateChange = new EventEmitter<SortEntry[]>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() rowReorder = new EventEmitter<{ previousIndex: number; currentIndex: number; data: T[] }>();
  @Output() cellEditSave = new EventEmitter<CellEditSaveEvent<T>>();

  editingCell: EditingCell<T> | null = null;

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private tablePrefs: TablePreferencesService
  ) {
    this.boundMouseMove = this.onResizeMouseMove.bind(this);
    this.boundMouseUp = this.onResizeMouseUp.bind(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.assertTableId();

    if (changes['paginationConfig'] && this.paginationConfig) {
      this.selectedPageSize = this.paginationConfig.pageSize;
    }

    if (changes['columns']) {
      this.initColumns();
    }

    if (changes['initialSort'] && this.initialSort && changes['initialSort'].firstChange) {
      this.sortState = [{ sortBy: this.initialSort.sortBy, direction: this.initialSort.direction }];
      this.sortData(this.initialSort.sortBy, this.initialSort.direction);
    }

    if (changes['errorMessage'] && this.errorMessage && this.errorMessage.trim()) {
      this.notificationService.error(this.errorMessage);
    }
  }

  ngAfterContentInit(): void {}

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  private assertTableId(): void {
    if (!this.tableId?.trim()) {
      throw new Error('app-data-table requiere tableId para persistir preferencias de tabla.');
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.editingCell) {
      return;
    }

    const column = this.localColumns.find(c => c.key === this.editingCell?.column);
    if (column?.editType !== 'multiSelect') {
      return;
    }

    const target = event.target as HTMLElement | null;
    const isInsideActiveEditor = target?.closest('.editing-cell, .inline-edit-popover');

    if (!isInsideActiveEditor) {
      this.cancelCellEdit();
    }
  }

  private initColumns(): void {
    const base = (this.columns ?? []).map(c => ({ ...c, visible: c.visible !== false }));

    if (this.tableId) {
      const prefs = this.tablePrefs.load(this.tableId);
      if (prefs.visibleColumns) {
        base.forEach(c => { c.visible = prefs.visibleColumns!.includes(c.key); });
      }
      if (prefs.columnWidths) {
        base.forEach(c => { if (prefs.columnWidths![c.key]) c.width = prefs.columnWidths![c.key]; });
      }
      if (prefs.density) {
        this.density = prefs.density;
      }
    }

    this.localColumns = base;
  }

  get displayedColumns(): string[] {
    return ['rowNumber', ...this.localColumns.filter(c => c.visible !== false).map(c => c.key)];
  }

  getRowNumber(index: number): number {
    return (this.paginationConfig ? (this.paginationConfig.currentPage - 1) * this.paginationConfig.pageSize : 0) + index + 1;
  }

  get renderColumns(): TableColumn[] {
    return this.localColumns.filter(c => c.visible !== false);
  }

  setColumnVisibility(key: string, visible: boolean): void {
    const col = this.localColumns.find(c => c.key === key);
    if (!col) return;
    col.visible = visible;
    this.localColumns = [...this.localColumns];
    if (this.tableId) {
      this.tablePrefs.patch(this.tableId, { visibleColumns: this.localColumns.filter(c => c.visible).map(c => c.key) });
    }
    this.cdr.markForCheck();
  }

  setDensity(density: 'compact' | 'normal' | 'comfortable'): void {
    this.density = density;
    if (this.tableId) {
      this.tablePrefs.patch(this.tableId, { density });
    }
    this.cdr.markForCheck();
  }

  setSort(sort: SortEntry): void {
    this.sortState = [{ ...sort }];
    this.sortData(sort.sortBy, sort.direction);
    this.cdr.markForCheck();
  }

  isCellEditable(column: TableColumn): boolean {
    return column.editable === true || this.editableColumns.includes(column.key);
  }

  isEditingCell(row: T, columnKey: string): boolean {
    return this.editingCell?.row === row && this.editingCell.column === columnKey;
  }

  isSavingCell(row: T, columnKey: string): boolean {
    if (!this.savingCell || this.savingCell.column !== columnKey) {
      return false;
    }

    const savingRow = this.savingCell.row;
    const currentId = (row as any)?.id;
    const savingId = (savingRow as any)?.id;

    return savingRow === row || (currentId != null && currentId === savingId);
  }

  onCellClick(event: MouseEvent, row: T, column: TableColumn): void {
    if (!this.isCellEditable(column) && !this.isSavingCell(row, column.key)) {
      return;
    }

    event.stopPropagation();

    if (this.isSavingCell(row, column.key)) {
      return;
    }

    this.startCellEdit(row, column);
  }

  startCellEdit(row: T, column: TableColumn): void {
    if (!this.isCellEditable(column) || this.isSavingCell(row, column.key)) {
      return;
    }

    const oldValue = this.getCellValue(row, column.key);
    this.editingCell = {
      row,
      column: column.key,
      oldValue,
      value: oldValue ?? ''
    };
    this.cdr.markForCheck();
    this.focusEditingControl();
  }

  setEditingValue(value: any): void {
    if (!this.editingCell) {
      return;
    }

    this.editingCell = { ...this.editingCell, value };
  }

  setMultiEditingValue(value: any, checked: boolean): void {
    const current = Array.isArray(this.editingValue) ? [...this.editingValue] : [];
    const next = checked
      ? [...current, value]
      : current.filter(item => String(item) !== String(value));

    this.setEditingValue(next);
  }

  commitCellEdit(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.editingCell) {
      return;
    }

    const edit = this.editingCell;
    const column = this.localColumns.find(c => c.key === edit.column);
    const newValue = this.normalizeEditValue(edit.value, column);
    this.editingCell = null;

    if (!this.areEditValuesEqual(newValue, edit.oldValue)) {
      this.cellEditSave.emit({
        row: edit.row,
        column: edit.column,
        oldValue: edit.oldValue,
        newValue
      });
    }

    this.cdr.markForCheck();
  }

  cancelCellEdit(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.editingCell = null;
    this.cdr.markForCheck();
  }

  get editingValue(): any {
    return this.editingCell?.value ?? '';
  }

  isEditOptionSelected(value: any): boolean {
    if (Array.isArray(this.editingValue)) {
      return this.editingValue.some(item => String(item) === String(value));
    }

    return String(value ?? '') === String(this.editingValue ?? '');
  }

  private getCellValue(row: T, columnKey: string): any {
    const column = this.localColumns.find(c => c.key === columnKey);
    return column?.editValue ? column.editValue(row) : (row as any)?.[columnKey];
  }

  private normalizeEditValue(value: any, column?: TableColumn): any {
    if (column?.editType === 'multiSelect') {
      return Array.isArray(value) ? value : [];
    }

    if (column?.editType !== 'number') {
      return value;
    }

    if (value === '' || value == null) {
      return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  }

  private areEditValuesEqual(value: any, oldValue: any): boolean {
    if (Array.isArray(value) || Array.isArray(oldValue)) {
      const current = Array.isArray(value) ? value.map(item => String(item)) : [];
      const previous = Array.isArray(oldValue) ? oldValue.map(item => String(item)) : [];

      if (current.length !== previous.length) {
        return false;
      }

      return current.every(item => previous.includes(item));
    }

    return value === oldValue;
  }

  private focusEditingControl(): void {
    setTimeout(() => {
      const control = this.el.nativeElement.querySelector('.inline-edit-control, .inline-edit-popover') as HTMLInputElement | HTMLSelectElement | HTMLElement | null;
      control?.focus();

      if (control instanceof HTMLInputElement) {
        control.select();
      }
    });
  }

  // ── Column reorder ────────────────────────────────────────────
  // ── Column resize ─────────────────────────────────────────────
  onResizeMouseDown(event: MouseEvent, colKey: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizeColKey = colKey;
    this.resizeStartX = event.clientX;
    const col = this.localColumns.find(c => c.key === colKey);
    this.resizeStartWidth = col?.width ? parseInt(col.width, 10) : this.getColumnElement(colKey)?.offsetWidth ?? 120;
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  private onResizeMouseMove(event: MouseEvent): void {
    if (!this.resizeColKey) return;
    const delta = event.clientX - this.resizeStartX;
    const col = this.localColumns.find(c => c.key === this.resizeColKey);
    if (!col) return;
    const min = col.minWidth ? parseInt(col.minWidth, 10) : 60;
    const newWidth = Math.max(min, this.resizeStartWidth + delta);
    col.width = `${newWidth}px`;
    this.localColumns = [...this.localColumns];
    this.cdr.markForCheck();
  }

  private onResizeMouseUp(_event: MouseEvent): void {
    if (this.resizeColKey && this.tableId) {
      const widths: Record<string, string> = {};
      this.localColumns.forEach(c => { if (c.width) widths[c.key] = c.width; });
      this.tablePrefs.patch(this.tableId, { columnWidths: widths });
    }
    this.resizeColKey = null;
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  private getColumnElement(key: string): HTMLElement | null {
    return this.el.nativeElement.querySelector(`th[data-col="${key}"]`);
  }

  // ── Sorting ───────────────────────────────────────────────────
  onHeaderClick(column: TableColumn, event?: MouseEvent): void {
    if (!this.enableHeaderSort || !column.sortable) return;

    if (this.multiSort && event?.shiftKey) {
      const existing = this.sortState.find(s => s.sortBy === column.key);
      if (existing) {
        if (existing.direction === 'asc') {
          existing.direction = 'desc';
        } else {
          this.sortState = this.sortState.filter(s => s.sortBy !== column.key);
        }
      } else {
        this.sortState = [...this.sortState, { sortBy: column.key, direction: 'asc' }];
      }
    } else {
      const current = this.sortState.find(s => s.sortBy === column.key);
      const newDir: 'asc' | 'desc' = current?.direction === 'asc' ? 'desc' : 'asc';
      this.sortState = [{ sortBy: column.key, direction: newDir }];
    }

    this.sortState = [...this.sortState];
    this.sortData(this.currentSortBy, this.currentSortDirection);
    this.sortStateChange.emit(this.sortState);
    if (this.sortState.length > 0) {
      this.sortChange.emit({ sortBy: this.sortState[0].sortBy, direction: this.sortState[0].direction });
    }
  }

  getSortIcon(column: TableColumn): string {
    const entry = this.sortState.find(s => s.sortBy === column.key);
    if (!entry) return '';
    return entry.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getSortIndex(column: TableColumn): number {
    if (!this.multiSort || this.sortState.length < 2) return -1;
    return this.sortState.findIndex(s => s.sortBy === column.key);
  }

  private sortData(sortBy: string, direction: 'asc' | 'desc'): void {
    this.dataSource.data = this.getSortedData(this.dataSource.data, sortBy, direction);
  }

  private getSortedData(data: T[], sortBy = this.currentSortBy, direction = this.currentSortDirection): T[] {
    if (this.serverSideSort || !sortBy) {
      return data;
    }

    const multiplier = direction === 'asc' ? 1 : -1;
    return [...data].sort((left, right) => multiplier * this.compareSortValues(
      (left as Record<string, unknown>)[sortBy],
      (right as Record<string, unknown>)[sortBy]
    ));
  }

  private compareSortValues(left: unknown, right: unknown): number {
    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    if (left instanceof Date && right instanceof Date) {
      return left.getTime() - right.getTime();
    }

    if (typeof left === 'number' && typeof right === 'number') {
      return left - right;
    }

    if (typeof left === 'boolean' && typeof right === 'boolean') {
      return Number(left) - Number(right);
    }

    return String(left).localeCompare(String(right), 'es', { numeric: true, sensitivity: 'base' });
  }

  // ── Row interaction ───────────────────────────────────────────
  onRowClick(row: T): void {
    this.rowClick.emit(row);
  }

  onRowDrop(event: any): void {
    const { previousIndex, currentIndex } = event;
    if (previousIndex === currentIndex) return;
    const newData = [...this.dataSource.data];
    const [movedItem] = newData.splice(previousIndex, 1);
    newData.splice(currentIndex, 0, movedItem);
    this.dataSource.data = newData;
    this.rowReorder.emit({ previousIndex, currentIndex, data: newData });
  }

  // ── Pagination ────────────────────────────────────────────────
  previousPage(): void {
    if (!this.paginationConfig || this.paginationConfig.currentPage <= 1) return;
    this.pageChange.emit({ page: this.paginationConfig.currentPage - 1 });
  }

  nextPage(): void {
    if (!this.paginationConfig || this.paginationConfig.currentPage >= this.paginationConfig.totalPages) return;
    this.pageChange.emit({ page: this.paginationConfig.currentPage + 1 });
  }

  onPageSizeChange(event: any): void {
    const newSize = typeof event.target.value === 'string' ? parseInt(event.target.value, 10) : event.target.value;
    this.pageSizeChange.emit({ pageSize: newSize });
  }

  // ── Getters ───────────────────────────────────────────────────
  get isFirstPage(): boolean {
    return !this.paginationConfig || this.paginationConfig.currentPage === 1;
  }

  get isLastPage(): boolean {
    return !this.paginationConfig || this.paginationConfig.currentPage >= this.paginationConfig.totalPages;
  }

  get displayedRange(): { start: number; end: number } {
    if (!this.paginationConfig) return { start: 0, end: 0 };
    const start = (this.paginationConfig.currentPage - 1) * this.paginationConfig.pageSize + 1;
    const end = Math.min(
      this.paginationConfig.currentPage * this.paginationConfig.pageSize,
      this.paginationConfig.totalCount
    );
    return { start, end };
  }

  get hasData(): boolean {
    return this.dataSource && this.dataSource.data && this.dataSource.data.length > 0;
  }

  get densityClass(): string {
    return `density-${this.density}`;
  }

  exportToCsv(filename = 'export.csv'): void {
    const visibleCols = this.localColumns.filter(c => c.visible !== false && c.exportable !== false);
    const headers = visibleCols.map(c => `"${c.header}"`).join(',');
    const rows = this.dataSource.data.map(row =>
      visibleCols.map(c => {
        const val = c.exportValue ? c.exportValue(row) : (row as any)[c.key] ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
